import {RegFormKeysBasedOnAccountT, getRegistrationSchema} from '@tnc/shared/schemas';
import {useRef} from 'react';
import {FormProvider} from 'react-hook-form';
import {useNavigate} from 'react-router';

import {AccountTypeT} from '@/lib/types/authentication.types';
import {FormStepT} from '@/lib/types/form.types';

import {brandFormFields, brandFormSteps} from '@/lib/forms/brand-form-config';
import {distributorFormFields, distributorFormSteps} from '@/lib/forms/distributor-form-config';
import {FormIds} from '@/lib/forms/form-ids';
import {retailerFormFields, retailerFormSteps} from '@/lib/forms/retailer-form-config';
import {speculatorFormFields, speculatorFormSteps} from '@/lib/forms/speculator-form-config';
import {getTermsFormFields, termsFormSteps} from '@/lib/forms/terms-form-config';

import {useAuth} from '@/providers/components/authProvider';
import {useAppForm} from '@/providers/components/formProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import FormFields from '@/components/form/FormFields';
import FormGlobalError from '@/components/form/FormGlobalError';
import FormStepsButtons from '@/components/form/FormStepsButtons';
import FormSubmitButton from '@/components/form/FormSubmitButton';

type RegistrationFormPropsT = {
  accountType?: AccountTypeT;
};

function RegistrationForm({accountType = 'speculator'}: RegistrationFormPropsT) {
  const {toast} = useNotifications();
  const navigate = useNavigate();
  const {
    handleRegister,
    userAccounts: {speculator, brands, retailers, distributors},
  } = useAuth();

  // Create stable form config reference
  const formConfigRef = useRef({
    // Fields
    fields: [
      ...speculatorFormFields,
      ...(accountType === 'brand' ? brandFormFields : []),
      ...(accountType === 'retailer' ? retailerFormFields : []),
      ...(accountType === 'distributor' ? distributorFormFields : []),
      ...getTermsFormFields(accountType),
    ],

    // Build field steps
    steps: (() => {
      // Add the speculator steps, by default, as all accounts have speculator fields, but
      // omit address fields
      const stepsArr = [
        ...speculatorFormSteps.filter(step => step.name !== 'address').map((step, index) => ({...step, step: index})),
      ];

      // Add additional steps based on account type, but also omit address fields
      switch (accountType) {
        case 'brand':
          stepsArr.push(
            ...brandFormSteps
              .filter(step => step.name !== 'address')
              .map((step, index) => ({...step, step: index + speculatorFormSteps.length}))
          );
          break;
        case 'retailer':
          stepsArr.push(
            ...retailerFormSteps
              .filter(step => step.name !== 'address')
              .map((step, index) => ({...step, step: index + speculatorFormSteps.length}))
          );
          break;
        case 'distributor':
          stepsArr.push(
            ...distributorFormSteps
              .filter(step => step.name !== 'address')
              .map((step, index) => ({...step, step: index + speculatorFormSteps.length}))
          );
          break;
      }

      // Add terms steps last
      const stepCount =
        speculatorFormSteps.length +
        (accountType === 'brand' ? brandFormSteps.length : 0) +
        (accountType === 'retailer' ? retailerFormSteps.length : 0) +
        (accountType === 'distributor' ? distributorFormSteps.length : 0);

      stepsArr.push(
        ...termsFormSteps.map((step, index) => ({
          ...step,
          step: index + stepCount,
        }))
      );

      return stepsArr;
    })(),

    // Default values
    defaultValues: {
      ...speculator,
      speculator_dob: speculator?.speculator_dob
        ? new Date(speculator.speculator_dob).toISOString().split('T')[0]
        : undefined,
      date_modified: new Date().toISOString(),
      ...(accountType === 'brand' ? brands : {}),
      ...(accountType === 'retailer' ? retailers : {}),
      ...(accountType === 'distributor' ? distributors : {}),
    },

    // Schema
    zodSchema: getRegistrationSchema(accountType),
  });

  type RegistrationFormKeysT = RegFormKeysBasedOnAccountT<typeof accountType>;

  // Initialize form with stable config
  const {formConfig, setGlobalError, setLoading, goToStep} = useAppForm<RegistrationFormKeysT>({
    formId: FormIds.REGISTRATION,
    config: formConfigRef.current,
  });

  const {handleSubmit, trigger, setError, reset} = formConfig;

  // Helper function to find the step containing a field
  const findStepWithField = (fieldName: string, steps: FormStepT<string>) => {
    return steps.findIndex(step => step.fields.includes(fieldName));
  };

  // Submit handler
  const onSubmit = async (data: RegistrationFormKeysT) => {
    try {
      setLoading(true);

      // Trigger validation for all fields
      const isValid = await trigger(undefined, {shouldFocus: true});

      if (!isValid) {
        window.scrollTo(0, 0);
        setGlobalError('Please check the form for errors and try again.');
        return;
      }

      // Validate with zod schema
      const validationResult = formConfigRef.current.zodSchema.safeParse(data);

      if (!validationResult.success) {
        window.scrollTo(0, 0);
        setGlobalError('Please fix the validation errors below.');

        // Find the first field with an error and its corresponding step
        const firstErrorField = validationResult.error.issues[0]?.path.join('.');
        if (firstErrorField && formConfigRef.current.steps) {
          const stepIndex = findStepWithField(firstErrorField, formConfigRef.current.steps);
          if (stepIndex !== -1) {
            goToStep(stepIndex);
          }
        }

        validationResult.error.issues.forEach(issue => {
          const path = issue.path.join('.');
          setError(path as keyof RegistrationFormKeysT, {
            type: 'validation',
            message: issue.message,
          });
        });

        return;
      }

      // Register the user
      await handleRegister<RegistrationFormKeysT>({
        data: validationResult.data,
        accountType,
        silentSignIn: true,
      });

      // All done!
      toast.success({
        title: 'Registration Complete!',
        message: 'You have been registered successfully.',
      });
      reset();
      navigate('/');
    } catch (error) {
      console.error({
        title: 'Registration Error',
        message: (error as Error).message ?? 'Something went wrong. Please try again later.',
      });
      setGlobalError((error as Error).message ?? 'Something went wrong. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormProvider {...formConfig}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6" noValidate>
        <FormGlobalError formId={FormIds.REGISTRATION} />
        <FormStepsButtons formId={FormIds.REGISTRATION} />
        <FormFields formId={FormIds.REGISTRATION} />
        <FormSubmitButton formId={FormIds.REGISTRATION} />
      </form>
    </FormProvider>
  );
}

export default RegistrationForm;
